import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  NewNavigationBar,
  FavoritesBar,
  Notification,
  TrackableLink,
} from '@surfline/quiver-react';
import {
  getUserPremiumStatus,
  getUserFreeTrialEligible,
  getUserEmailVerified,
} from '@surfline/web-common';
import HeroAdUnit from './HeroAdUnit';
import AdBlock from './AdBlock';
import NotificationBarController from '../NotificationBarController';
import { CLICKED_SUBSCRIBE_CTA } from '../../../common/constants';
import {
  HERO_AD_PLACEHOLDER,
  SL_WEB_MULTI_CAM_MODAL,
  SL_WEB_SPIRIT_OF_SURFING_NAV,
} from '../../../common/treatments';
import {
  useEntitlementsSelector,
  useUserDetailsSelector,
  useUserSettingsSelector,
  useUserFavoritesSelector,
  useSubscriptionWarningsSelector,
} from '../../../selectors/user';
import { heroAdConfigMap } from './heroAdConfig';
import config from '../../../config';

const SUBSCRIBE_CTA_PROPERTIES = { ctaLocation: 'button top' };

const Header = ({
  renderOptions,
  openHouse,
  features: {
    treatments: {
      [HERO_AD_PLACEHOLDER]: heroAdPlaceholder,
      [SL_WEB_MULTI_CAM_MODAL]: multiCamModalTreatment,
      [SL_WEB_SPIRIT_OF_SURFING_NAV]: spiritOfSurfingNavLink,
    },
  },
}) => {
  const freeTrialLinkRef = useRef();
  const user = useUserDetailsSelector();
  const settings = useUserSettingsSelector();
  const favorites = useUserFavoritesSelector();
  const entitlements = useEntitlementsSelector();
  const isPremium = useSelector((state) => getUserPremiumStatus(state, true));
  const freeTrialEligible = useSelector(getUserFreeTrialEligible);
  const warnings = useSubscriptionWarningsSelector();

  // Check if an alert should be displayed to the current user
  const isEmailVerified = useSelector(getUserEmailVerified);
  const accountAlert = user && !isEmailVerified;

  // Check if a warning should be displayed to the current user
  const accountWarning = !!warnings.length;

  const largeFavoritesBar = !!renderOptions.largeFavoritesBar;
  const hideCTA = renderOptions.hideCTA === 'true';
  const hideNav = renderOptions.hideNav === 'true';

  const smallNotification = (
    <Notification type="small" level="information">
      <TrackableLink
        ref={freeTrialLinkRef}
        eventName={CLICKED_SUBSCRIBE_CTA}
        eventProperties={SUBSCRIBE_CTA_PROPERTIES}
      >
        <a ref={freeTrialLinkRef} href="/upgrade" className="quiver-notification__cta">
          START FREE TRIAL
        </a>
      </TrackableLink>
    </Notification>
  );

  return (
    <div>
      {!isPremium && renderOptions.showAdBlock === 'true' ? <AdBlock /> : null}
      {renderOptions.bannerAd !== 'false' && heroAdConfigMap[renderOptions.bannerAd] ? (
        <HeroAdUnit
          adType={renderOptions.bannerAd}
          user={user}
          entitlements={entitlements}
          qaFlag={renderOptions.qaFlag}
          placeholder={heroAdPlaceholder}
          articleId={renderOptions.bannerAdId}
        />
      ) : null}
      {!hideNav ? (
        <div>
          {!hideCTA ? (
            <NotificationBarController
              isPremium={isPremium}
              openHouse={openHouse}
              user={user}
              warnings={warnings}
            />
          ) : null}
          <NewNavigationBar
            user={user}
            favorites={favorites}
            settings={settings}
            smallNotification={smallNotification}
            hideCTA={hideCTA}
            entitlements={entitlements}
            serviceConfig={{
              serviceUrl: config.servicesURL,
            }}
            freeTrialEligible={freeTrialEligible}
            accountAlert={accountAlert}
            accountWarning={accountWarning}
            spiritOfSurfingNavLink={spiritOfSurfingNavLink}
          />
          <FavoritesBar
            settings={settings}
            favorites={favorites}
            user={user}
            large={largeFavoritesBar}
            multiCamModalEnabled={multiCamModalTreatment === 'hp_modal'}
          />
          <div id="backplane-header-open-house" />
        </div>
      ) : null}
    </div>
  );
};

Header.propTypes = {
  renderOptions: PropTypes.shape({
    largeFavoritesBar: PropTypes.string,
    qaFlag: PropTypes.string,
    bannerAd: PropTypes.string,
    hideCTA: PropTypes.oneOf(['true', 'false']),
    hideNav: PropTypes.oneOf(['true', 'false']),
    showAdBlock: PropTypes.oneOf(['true', 'false']),
    bannerAdId: PropTypes.string,
  }),
  openHouse: PropTypes.shape({
    active: PropTypes.bool.isRequired,
    data: PropTypes.shape({
      banner: PropTypes.shape({
        copy: PropTypes.string.isRequired,
        creative: PropTypes.shape({
          '1x': PropTypes.string.isRequired,
          '2x': PropTypes.string,
          '3x': PropTypes.string,
        }).isRequired,
      }),
    }),
  }).isRequired,
  features: PropTypes.shape({
    treatments: PropTypes.shape({
      hero_ad_placeholder: PropTypes.string,
    }),
  }).isRequired,
};

Header.defaultProps = {
  renderOptions: {
    largeFavoritesBar: null,
    qaFlag: null,
    bannerAd: 'false',
    hideCTA: 'false',
    hideNav: 'false',
  },
};

export default Header;
